import React, { createContext, useContext, useEffect, useState } from "react"

import { formatPhone, urqlClient } from "../utils/utils"

const PRACTICE_QUERY = `
  query CurrentPracticeContext {
    currentPractice {
      id
      recurringBookingsEnabled
      packagesEnabled
      clientNotesEnabled
      bookableEventsEnabled
      headline
      websiteHeadline
      subHeadline
      ctaButtonText
      valueStatement
      quote
      quoteAuthor
      defaultServiceId
      headerHeroImageUrl
      footerHeroImageUrl
      servicesSectionImageUrl
      reviewsSectionImageUrl
      valueStatementImageUrl
      valueStatementImageTwoUrl
      about
      website
      facebook
      instagram
      twitter
      linkedin
      tiktok
      youtube
      defaultCurrency
      city
      stateAbbreviation
      country
      countryCode
      user {
        id
        name
        firstName
        lastName
        title
        slug
        vanitySlug
        phone
        email
        profilePhotos {
          large {
            any
            jpeg
            webp
          }
          medium {
            any
            jpeg
            webp
          }
          small {
            any
            jpeg
            webp
          }
        }
      }
      hasPaymentProvider
      paymentProvider
      url
      published
      active
      reviewsAverage
      reviewsCount
      customDomain
      conferencingServices {
        provider
        active
      }
      locations {
        id
        name
        singleLine
        kind
        displayAddress
        formattedTimeZone
        timeZoneAbbr
      }
      services {
        id
        name
        description
        timeLength
        amountCents
        numberOfSessions
        package
        photoUrl
        locations {
          id
          kind
          formattedTimeZone
          timeZoneAbbr
          displayAddress
        }
      }
      galleryImages {
        id
        defaultPhotoUrl
      }
      reviews {
        id
        rating
        review
        createdAt
        date
        authorDescription
        authorName
        client {
          firstName
          lastName
        }
      }
      proCredentials {
        position
        licenseNumber
        stateIssued
        kind
        filestackDocument
        filestackDocumentName
        issuedAt
        expiresAt
        credential {
          name
        }
      }
      skills {
        position
        endorsementsCount
        specialty {
          id
          name
        }
      }
      languages {
        name
      }
      workWiths {
        name
      }
      ageWorkWiths {
        name
      }
      imageAssets {
        id
        altText
        description
        resizedImageUrl
      }
    }
  }
`

export const PracticeContext = createContext()

export const PracticeProvider = ({ children }) => {
  const [practice, setPractice] = useState({})
  const [practiceServices, setPracticeServices] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const fetchPractice = () => {
    setIsLoading(true)
    urqlClient
      .query(PRACTICE_QUERY)
      .toPromise()
      .then((res) => {
        if (res.data?.currentPractice) {
          setPractice(res.data.currentPractice)
        } else if (res.error) {
          console.error("GraphQL error fetching practice data:", res.error) // eslint-disable-line no-console
          // If it's an authentication error, don't show it as an error
          if (res.error.message?.includes("Not authorized") || res.error.message?.includes("Authentication")) {
            console.log("User not authenticated, this is expected for public pages") // eslint-disable-line no-console
          }
        } else {
          console.error("Error fetching practice data - no data or error returned:", res) // eslint-disable-line no-console
        }
      })
      .catch((error) => {
        console.error("Network error fetching practice data:", error) // eslint-disable-line no-console
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    fetchPractice()
  }, [])

  const practiceEmail = practice.user?.email
  const practicePhone = formatPhone(practice.user?.phone)

  return (
    <PracticeContext.Provider
      value={{
        practice,
        setPractice,
        practiceEmail,
        practicePhone,
        fetchPractice,
        practiceServices,
        setPracticeServices,
        isLoading
      }}>
      {children}
    </PracticeContext.Provider>
  )
}

export const usePractice = () => useContext(PracticeContext)
